import * as i0 from '@angular/core';
import { Injectable, Inject, NgModule } from '@angular/core';
import * as i1$1 from '@ngrx/effects';
import { createEffect, ofType, EffectsModule } from '@ngrx/effects';
import * as i6 from '@ngrx/store';
import { createAction, props, createFeatureSelector, createSelector, select, createReducer, on, StoreModule } from '@ngrx/store';
import { map, from, switchMap, catchError, of } from 'rxjs';
import { HttpHeaders } from '@angular/common/http';
import * as i3 from '@upr-web-primer/dal';
import * as i1 from '@upr-web-primer/gds-button-model-service';
import * as i4 from '@upr-web-primer/locale';
import * as i5 from '@upr-web-primer/page-labels';
import { WINDOW } from '@upr-web-primer/window';
import { get } from 'lodash-es';
import * as i2 from 'ngx-cookie-service';

const NotificationsFeatureKey = 'gds-notifications';
const DEFAULT_MAX_PRIMARY_NOTIFICATIONS = 2;
const DEFAULT_READ_STATUS_DURATION = 3;
const READ_GDS_NOTIFICATIONS_KEY = 'read-gds-notifications';
var TriggerTypes;
(function (TriggerTypes) {
    TriggerTypes["DEFAULT"] = "default";
    TriggerTypes["PAGE"] = "page";
    TriggerTypes["GEO"] = "geo";
    TriggerTypes["USER"] = "user";
})(TriggerTypes || (TriggerTypes = {}));

const LoadContent = createAction('[Content] Load Notification Content', props());
const LoadContentSuccess = createAction('[Content] Load Notification Content Success', props());
const LoadContentFailed = createAction('[Content] Load Notification Content Failed', props());
const ParseContent = createAction('[Content] Parse Notification Content', props());
const ParseContentSuccess = createAction('[Content] Parse Notification Content Success', props());
const ReadNotifications = createAction('[Notifications] Read Notifications', props());

const notificationsFeatureState = createFeatureSelector(NotificationsFeatureKey);
const getNotificationsData = createSelector(notificationsFeatureState, (state) => ({
    notifications: get(state, 'notifications', []),
    maxPrimaryNotifications: get(state, 'maxPrimaryNotifications', DEFAULT_MAX_PRIMARY_NOTIFICATIONS),
}));

class GdsNotificationsService {
    constructor(buttonModelService, cookieService, dal, localeService, pageLabelsResolver, $store, appDefaults, relativeUrls, window) {
        this.buttonModelService = buttonModelService;
        this.cookieService = cookieService;
        this.dal = dal;
        this.localeService = localeService;
        this.pageLabelsResolver = pageLabelsResolver;
        this.$store = $store;
        this.appDefaults = appDefaults;
        this.relativeUrls = relativeUrls;
        this.window = window;
        this.storedReadNotificationIds = this.getReadNotificationsCookies();
        this.$notifications = this.$store.pipe(select(getNotificationsData), map((data) => ({
            notifications: data.notifications.filter((notification) => notification.isTriggered),
            maxPrimaryNotifications: data.maxPrimaryNotifications,
        })));
        this.pageLabelsResolver.get().then((pageLabels) => {
            this.pageLabels = pageLabels;
        });
    }
    getContentById(contentId) {
        const locale = this.localeService.getFromStorage();
        const language = locale?.language;
        const country = locale?.country?.toUpperCase();
        const httpOptions = new HttpHeaders({
            'Accept-Language': `${language}-${country}` + (language === 'es' ? ',en-US' : ''),
        });
        const relativeUrl = this.replaceCode(this.relativeUrls.FETCH_CONTENT) + contentId;
        return from(this.dal.httpRequest(relativeUrl, 'GET', {}, httpOptions));
    }
    getNotifications(contentId) {
        this.$store.dispatch(LoadContent({ contentId }));
    }
    setNotifications(tridionData) {
        this.$store.dispatch(ParseContent({ content: tridionData }));
    }
    parseNotificationsData(rawData) {
        const notifications = Array.isArray(rawData) ? rawData[0] : rawData;
        const notificationsData = get(notifications, 'data', notifications);
        const globalNotifications = this.parseNotifications(get(notificationsData, 'global', []));
        const appNotifications = this.parseAppNotifications(get(notificationsData, 'app', []));
        let maxPrimaryNotifications = get(notificationsData, 'maxPrimaryNotifications', DEFAULT_MAX_PRIMARY_NOTIFICATIONS);
        maxPrimaryNotifications = Number.isNaN(Number(maxPrimaryNotifications))
            ? DEFAULT_MAX_PRIMARY_NOTIFICATIONS
            : Number(maxPrimaryNotifications);
        const readStatusDuration = get(notificationsData, 'readStatusDuration', DEFAULT_READ_STATUS_DURATION);
        this.readStatusDuration = Number.isNaN(Number(readStatusDuration))
            ? DEFAULT_MAX_PRIMARY_NOTIFICATIONS
            : Number(readStatusDuration);
        this.notificationsData = {
            notifications: globalNotifications.concat(appNotifications),
            maxPrimaryNotifications,
        };
        return this.notificationsData;
    }
    readNotifications(ids) {
        if (ids.length === 0) {
            return;
        }
        const allReadIds = [];
        const notifications = this.notificationsData.notifications.map((notification) => {
            const id = notification.id;
            let isRead = notification.isRead;
            if (isRead || ids.includes(id)) {
                allReadIds.push(id);
                isRead = true;
            }
            return {
                ...notification,
                isRead,
            };
        });
        this.notificationsData = {
            notifications: notifications,
            maxPrimaryNotifications: this.notificationsData.maxPrimaryNotifications,
        };
        this.$store.dispatch(ReadNotifications({ notifications }));
        this.setReadNotificationsCookie(allReadIds);
    }
    setReadNotificationsCookie(ids) {
        const date = new Date();
        date.setTime(date.getTime() + 1000 * 60 * 60 * 24 * this.readStatusDuration);
        this.cookieService.set(READ_GDS_NOTIFICATIONS_KEY, ids.join(','), date, '/');
    }
    getReadNotificationsCookies() {
        return this.cookieService.get(READ_GDS_NOTIFICATIONS_KEY).split(',');
    }
    replaceCode(url) {
        return url
            ? url.replace('{resortAreaCode}', this.appDefaults.PERSONALIZE_RESORT_CODE)
            : '';
    }
    parseNotifications(data, appTypes) {
        return data.map((notification, index) => {
            const isTriggered = this.checkTriggerRule(notification.trigger_rules, appTypes);
            const displayClose = notification.closeButtonToggle
                ? [true, 'true'].includes(notification.closeButtonToggle)
                : false;
            const button = this.buttonModelService.mapButtonData(notification.button);
            const type = get(notification, 'notificationType', 'secondary');
            const id = type +
                '-' +
                get(notification, 'description', '')
                    .substring(0, 6)
                    .trim()
                    .replace(/[, ]/g, '_') +
                '-' +
                index;
            const closeAriaLabel = get(this.pageLabels, ['notification.close.label'], '');
            return {
                id,
                type: notification.notificationType,
                body: {
                    description: notification.description,
                },
                button,
                closeButton: {
                    ariaLabel: closeAriaLabel,
                },
                displayClose,
                isRead: this.storedReadNotificationIds.includes(id),
                isTriggered,
                triggerRules: notification.trigger_rules,
            };
        }, []);
    }
    parseAppNotifications(data) {
        let appNotifications = [];
        data.forEach((notifications) => {
            if (!notifications.appNotifications) {
                return;
            }
            const rawAppType = get(notifications, 'appType', 'global');
            let appTypes = [];
            try {
                appTypes = JSON.parse(rawAppType);
            }
            catch (e) {
                appTypes = [rawAppType];
            }
            const currAppNotifications = Array.isArray(notifications.appNotifications)
                ? notifications.appNotifications
                : [notifications.appNotifications];
            appNotifications = appNotifications.concat(this.parseNotifications(currAppNotifications, appTypes));
        }, []);
        return appNotifications;
    }
    checkAppTypes(types) {
        if (!types) {
            return true;
        }
        const path = this.window.location.pathname;
        return types.some((type) => {
            switch (type) {
                case 'guest-account':
                    return path.includes('/account/');
                case 'hhn-microsite':
                    return path.includes('/hhn/');
                case 'hotels-packages':
                    return path.includes('/web-packages/');
                case 'web-store':
                    return path.includes('/web-store/');
                case 'web-content':
                    return path.includes('/web/');
                case 'global':
                    return true;
                default:
                    return path.includes(`/${type}/`);
            }
        });
    }
    checkTriggerRule(rules, appTypes) {
        const isAppType = this.checkAppTypes(appTypes);
        if (!isAppType) {
            return false;
        }
        else if (!rules) {
            return true;
        }
        const parsedRules = Array.isArray(rules) ? rules : [rules];
        return parsedRules.some((rule) => {
            switch (rule.type) {
                case TriggerTypes.DEFAULT:
                    return true;
                case TriggerTypes.PAGE:
                    return this.checkPage(rule.condition);
                default:
                    return false;
            }
        });
    }
    checkPage(condition) {
        if (!condition)
            return false;
        const path = this.window.location.pathname;
        try {
            const regex = RegExp(condition);
            return regex.test(path);
        }
        catch (error) {
            return false;
        }
    }
}
GdsNotificationsService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: GdsNotificationsService, deps: [{ token: i1.GdsButtonModelService }, { token: i2.CookieService }, { token: i3.DalService }, { token: i4.LocaleService }, { token: i5.PageLabelsResolver }, { token: i6.Store }, { token: 'AppDefaults' }, { token: 'RelativeUrls' }, { token: WINDOW }], target: i0.ɵɵFactoryTarget.Injectable });
GdsNotificationsService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: GdsNotificationsService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: GdsNotificationsService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.GdsButtonModelService }, { type: i2.CookieService }, { type: i3.DalService }, { type: i4.LocaleService }, { type: i5.PageLabelsResolver }, { type: i6.Store }, { type: undefined, decorators: [{
                    type: Inject,
                    args: ['AppDefaults']
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: ['RelativeUrls']
                }] }, { type: Window, decorators: [{
                    type: Inject,
                    args: [WINDOW]
                }] }]; } });

class GdsNotificationsEffects {
    constructor(actions$, notifications) {
        this.actions$ = actions$;
        this.notifications = notifications;
        this.loadContent$ = createEffect(() => this.actions$.pipe(ofType(LoadContent), switchMap((action) => {
            const obs$ = this.notifications.getContentById(action.contentId);
            return obs$.pipe(map((content) => LoadContentSuccess({
                content: content || [],
            })), catchError((error) => {
                return of(LoadContentFailed({
                    error: error,
                }));
            }));
        })));
        this.parseContent$ = createEffect(() => this.actions$.pipe(ofType(ParseContent, LoadContentSuccess), switchMap((action) => of(ParseContentSuccess({
            content: this.notifications.parseNotificationsData(action.content),
        })))));
    }
}
GdsNotificationsEffects.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: GdsNotificationsEffects, deps: [{ token: i1$1.Actions }, { token: GdsNotificationsService }], target: i0.ɵɵFactoryTarget.Injectable });
GdsNotificationsEffects.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: GdsNotificationsEffects });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: GdsNotificationsEffects, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: i1$1.Actions }, { type: GdsNotificationsService }]; } });

const reducers = createReducer({}, on(ParseContentSuccess, (state, { content: { notifications, maxPrimaryNotifications } }) => ({
    ...state,
    notifications,
    maxPrimaryNotifications,
})), on(ReadNotifications, (state, { notifications }) => ({
    ...state,
    notifications,
})));

class GdsNotificationsModule {
}
GdsNotificationsModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: GdsNotificationsModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
GdsNotificationsModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: GdsNotificationsModule, imports: [i6.StoreFeatureModule, i1$1.EffectsFeatureModule] });
GdsNotificationsModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: GdsNotificationsModule, imports: [[
            StoreModule.forFeature(NotificationsFeatureKey, reducers),
            EffectsModule.forFeature([GdsNotificationsEffects]),
        ]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: GdsNotificationsModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [],
                    imports: [
                        StoreModule.forFeature(NotificationsFeatureKey, reducers),
                        EffectsModule.forFeature([GdsNotificationsEffects]),
                    ],
                    exports: [],
                }]
        }] });

export { DEFAULT_MAX_PRIMARY_NOTIFICATIONS, DEFAULT_READ_STATUS_DURATION, GdsNotificationsModule, GdsNotificationsService, NotificationsFeatureKey, READ_GDS_NOTIFICATIONS_KEY, TriggerTypes };
